/**
 * Tâche 1220 (Eric 2026-06-26) — Journal des rejets de doublons dans la fiche
 * d'un terme.
 *
 * Eric doit pouvoir répondre à « pourquoi ce doublon évident n'est jamais
 * proposé ? » sans ouvrir la fenêtre d'examen des doublons : la réponse (qui a
 * rejeté, quand, et avec quel terme) s'affiche directement dans la fiche, avec
 * la possibilité d'annuler le rejet pour que le couple soit reproposé.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowCounterClockwise, Prohibit } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TermRejectionLog({ termId }) {
  const [rejections, setRejections] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!termId) return;
    try {
      const { data } = await axios.get(
        `${API}/api/jargon/duplicates/rejections/for-term/${termId}`,
        { withCredentials: true },
      );
      setRejections(data?.rejections || []);
    } catch (_) {
      setRejections([]);
    } finally {
      setLoaded(true);
    }
  }, [termId]);

  useEffect(() => {
    setLoaded(false);
    load();
  }, [load]);

  const undo = async (pairKey) => {
    setBusy(pairKey);
    try {
      await axios.delete(
        `${API}/api/jargon/duplicates/rejections/${encodeURIComponent(pairKey)}`,
        { withCredentials: true },
      );
      toast.success("Rejet annulé — ce couple sera reproposé à la prochaine détection.");
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  if (!termId || !loaded || rejections.length === 0) return null;

  return (
    <div
      className="rounded-md border border-zinc-200 bg-zinc-50 p-3"
      data-testid="term-rejection-log"
    >
      <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-600 flex items-center gap-1.5">
        <Prohibit size={13} />
        Doublons rejetés pour ce terme ({rejections.length})
      </h4>
      <p className="text-[11px] text-zinc-500 mt-1">
        Ces couples ne seront plus proposés par la détection de doublons. C&apos;est
        souvent la raison pour laquelle un doublon « évident » n&apos;apparaît jamais.
      </p>
      <ul className="mt-2 space-y-1">
        {rejections.map((r) => (
          <li
            key={r.pair_key}
            className="flex items-start justify-between gap-2 text-xs bg-white border border-zinc-200 rounded px-2 py-1.5"
            data-testid={`term-rejection-row-${r.pair_key}`}
          >
            <span className="min-w-0 break-words">
              <strong className="text-zinc-800">{r.other_term || "(terme supprimé)"}</strong>
              <span className="text-zinc-500">
                {" — rejeté par "}
                {r.rejected_by || "inconnu"}
                {r.rejected_at
                  ? ` le ${new Date(r.rejected_at).toLocaleString("fr-CA", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}`
                  : ""}
              </span>
            </span>
            <button
              type="button"
              onClick={() => undo(r.pair_key)}
              disabled={busy === r.pair_key}
              className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
              data-testid={`term-rejection-undo-${r.pair_key}`}
            >
              <ArrowCounterClockwise size={12} />
              Annuler
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
